(() => {
  const STORAGE_KEY = 'muzdan:wishlist:v1';
  const CHANGE_EVENT = 'muzdan:wishlist:change';

  const readWishlist = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.id) : [];
    } catch (error) {
      return [];
    }
  };

  const writeWishlist = (products) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(products));
    } catch (error) {
      // The UI remains usable when storage is unavailable in private browsing.
    }

    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, { detail: { products } })
    );
  };

  const productIsSaved = (id) =>
    readWishlist().some((product) => String(product.id) === String(id));

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.control = this.querySelector('[data-wishlist-toggle]');
      this.dataNode = this.querySelector('[data-wishlist-product]');

      if (!this.control || !this.dataNode) return;

      try {
        this.product = JSON.parse(this.dataNode.textContent);
      } catch (error) {
        return;
      }

      this.onClick = this.handleClick.bind(this);
      this.onChange = this.sync.bind(this);
      this.control.addEventListener('click', this.onClick);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      this.sync();
    }

    disconnectedCallback() {
      this.control?.removeEventListener('click', this.onClick);
      document.removeEventListener(CHANGE_EVENT, this.onChange);
    }

    handleClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const products = readWishlist();
      const index = products.findIndex(
        (product) => String(product.id) === String(this.product.id)
      );

      if (index === -1) {
        products.unshift({ ...this.product, savedAt: Date.now() });
      } else {
        products.splice(index, 1);
      }

      writeWishlist(products);
    }

    sync() {
      if (!this.product || !this.control) return;

      const saved = productIsSaved(this.product.id);
      this.classList.toggle('is-saved', saved);
      this.control.setAttribute('aria-pressed', String(saved));
      this.control.setAttribute(
        'aria-label',
        `${saved ? 'Remove' : 'Add'} ${this.product.title} ${saved ? 'from' : 'to'} wishlist`
      );

      const label = this.querySelector('[data-wishlist-label]');
      if (label) label.textContent = saved ? 'Saved to wishlist' : 'Add to wishlist';
    }
  }

  class WishlistDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-wishlist-panel]');
      this.items = this.querySelector('[data-wishlist-items]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.previouslyFocused = null;
      this.closeTimer = null;

      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onKeydown = this.handleKeydown.bind(this);
      this.onChange = this.render.bind(this);

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener(CHANGE_EVENT, this.onChange);
      window.addEventListener('storage', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener(CHANGE_EVENT, this.onChange);
      window.removeEventListener('storage', this.onChange);
    }

    handleDocumentClick(event) {
      const openTrigger = event.target.closest('[data-wishlist-open]');
      if (openTrigger) {
        event.preventDefault();
        this.open(openTrigger);
        return;
      }

      if (event.target.closest('[data-wishlist-close]')) {
        event.preventDefault();
        this.close();
        return;
      }

      const addTrigger = event.target.closest('[data-wishlist-add]');
      if (addTrigger) {
        event.preventDefault();
        this.addToCart(addTrigger);
        return;
      }

      const removeTrigger = event.target.closest('[data-wishlist-remove]');
      if (removeTrigger) {
        event.preventDefault();
        const id = removeTrigger.dataset.wishlistRemove;
        writeWishlist(
          readWishlist().filter((product) => String(product.id) !== String(id))
        );
      }
    }

    async resolveVariantId(trigger) {
      if (trigger.dataset.variantId) return trigger.dataset.variantId;
      if (!trigger.dataset.productUrl) return null;

      const productUrl = new URL(trigger.dataset.productUrl, window.location.origin);
      productUrl.pathname = `${productUrl.pathname.replace(/\/$/, '')}.js`;
      const response = await fetch(productUrl.toString(), {
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) return null;

      const product = await response.json();
      const variantId = product.variants?.find((variant) => variant.available)?.id;
      if (variantId) trigger.dataset.variantId = variantId;
      return variantId;
    }

    setAddedState(trigger) {
      const title = trigger.closest('.wishlist-drawer__item')?.querySelector('.wishlist-drawer__title')?.textContent || 'product';
      trigger.textContent = 'Added to cart';
      trigger.disabled = true;
      trigger.classList.add('is-added');
      trigger.setAttribute('aria-label', `Added ${title} to cart`);
    }

    async refreshCartDrawerSilently() {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer?.getSectionsToRender?.() || [];
      if (!cartDrawer || !sections.length) return;

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sections.map((section) => section.section).join(','));
        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        if (!response.ok) return;

        const refreshedSections = await response.json();
        cartDrawer.renderContents({ sections: refreshedSections }, false);
      } catch (error) {
        console.error('Wishlist cart drawer refresh failed:', error);
      }
    }

    async addToCart(trigger) {
      if (trigger.disabled) return;

      const originalLabel = trigger.textContent;
      trigger.disabled = true;
      trigger.textContent = 'Adding…';

      try {
        const variantId = await this.resolveVariantId(trigger);
        if (!variantId) throw new Error('This product is currently unavailable.');

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(routes.cart_add_url, config);
        const state = await response.json();

        if (!response.ok || state.status || state.errors) {
          throw new Error(state.description || state.errors || 'Unable to add this product to the cart.');
        }

        this.setAddedState(trigger);
        if (typeof updateCartCounters === 'function') updateCartCounters();
        if (typeof updateFreeShipping === 'function') updateFreeShipping();
        this.refreshCartDrawerSilently();
      } catch (error) {
        console.error('Wishlist add to cart failed:', error);
        trigger.textContent = 'Try again';
        trigger.setAttribute('aria-label', error.message);
        window.setTimeout(() => {
          trigger.textContent = originalLabel;
          trigger.disabled = false;
        }, 2000);
      }
    }

    async syncCartState() {
      try {
        const response = await fetch(`${routes.cart_url}.json`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;

        const cart = await response.json();
        const variantIds = new Set(cart.items.map((item) => String(item.variant_id)));

        this.querySelectorAll('[data-wishlist-add]').forEach((button) => {
          if (button.dataset.productAvailable === 'false') return;

          const isInCart = button.dataset.variantId && variantIds.has(String(button.dataset.variantId));
          if (isInCart) {
            this.setAddedState(button);
          } else {
            button.textContent = 'Add to cart';
            button.disabled = false;
            button.classList.remove('is-added');
          }
        });
      } catch (error) {
        // Wishlist remains usable if cart state cannot be read.
      }
    }

    handleKeydown(event) {
      if (event.key === 'Escape' && this.classList.contains('is-open')) {
        this.close();
      }
    }

    open(trigger) {
      window.clearTimeout(this.closeTimer);
      this.previouslyFocused = trigger || document.activeElement;
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('wishlist-drawer-open');
      this.syncCartState();
      requestAnimationFrame(() => {
        this.classList.add('is-open');
        this.querySelector('[data-wishlist-close-button]')?.focus();
      });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('wishlist-drawer-open');
      this.previouslyFocused?.focus?.();
      this.closeTimer = window.setTimeout(() => {
        if (!this.classList.contains('is-open')) this.hidden = true;
      }, 450);
    }

    render() {
      const products = readWishlist();

      document.querySelectorAll('[data-wishlist-count]').forEach((count) => {
        count.textContent = products.length;
        count.hidden = products.length === 0;
      });

      document.querySelectorAll('[data-wishlist-open]').forEach((trigger) => {
        trigger.setAttribute(
          'aria-label',
          `Open wishlist${products.length ? `, ${products.length} item${products.length === 1 ? '' : 's'}` : ''}`
        );
      });

      if (!this.items || !this.empty) return;

      this.items.replaceChildren();
      this.empty.hidden = products.length !== 0;

      products.forEach((product) => {
        const article = document.createElement('article');
        article.className = 'wishlist-drawer__item';

        const link = document.createElement('a');
        link.className = 'wishlist-drawer__image media';
        link.href = product.url;
        link.setAttribute('aria-label', `View ${product.title}`);

        if (product.image) {
          const image = document.createElement('img');
          image.src = product.image;
          image.alt = product.title;
          image.loading = 'lazy';
          link.append(image);
        }

        const details = document.createElement('div');
        details.className = 'wishlist-drawer__details';

        const title = document.createElement('a');
        title.className = 'wishlist-drawer__title';
        title.href = product.url;
        title.textContent = product.title;

        const price = document.createElement('span');
        price.className = 'wishlist-drawer__price';
        price.textContent = product.price;

        const actions = document.createElement('div');
        actions.className = 'wishlist-drawer__actions';

        const view = document.createElement('a');
        view.className = 'wishlist-drawer__view text-link';
        view.href = product.url;
        view.textContent = 'View product';

        const add = document.createElement('button');
        add.type = 'button';
        add.className = 'wishlist-drawer__add';
        add.dataset.wishlistAdd = product.id;
        add.dataset.variantId = product.variantId || '';
        add.dataset.productUrl = product.url || '';
        add.dataset.productAvailable = String(product.available !== false);
        add.textContent = product.available === false ? 'Sold out' : 'Add to cart';
        add.disabled = product.available === false;
        add.setAttribute('aria-label', `${add.textContent} ${product.title}`);

        actions.append(view, add);
        details.append(title, price, actions);

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'wishlist-drawer__remove';
        remove.dataset.wishlistRemove = product.id;
        remove.setAttribute('aria-label', `Remove ${product.title} from wishlist`);
        remove.innerHTML = '<span aria-hidden="true">×</span>';

        article.append(link, details, remove);
        this.items.append(article);
      });

      this.syncCartState();
    }
  }

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', WishlistButton);
  }

  if (!customElements.get('wishlist-drawer')) {
    customElements.define('wishlist-drawer', WishlistDrawer);
  }
})();
